// Imports for external libraries go here.
import React, { FC } from 'react';
import clsx from 'clsx';

// Imports for internal (to the monorepo) libraries go here,
// The closer the import is to the file the lower it should be in this list.
import { Video } from '@marriott/mi-ui-library';
import { URL_CONSTANTS, DESKTOP_MEDIA_QUERY } from '../../../utils';
import { useMediaQuery } from '../../../hooks';
import { HeroBannerVideoProps } from './HeroBannerVideo.types';
import { StyledHeroBannerVideo } from './HeroBannerVideo.styles';

const HeroBannerVideo: FC<HeroBannerVideoProps> = props => {
  const {
    playerId,
    videoaccountId,
    videoIdDesktop,
    videoIdMobile,
    videoEnabledDesktop,
    videoEnabledMobile,
    enableAutoPlay,
    enableVideoLoop,
    enableMute,
    fileReferenceVideoCoverImage,
    playerDomain,
    location,
    aspectRatioMobileVideo,
    clickTrack,
    hasDesktopVideoError,
    hasMobileVideoError,
    setHasDesktopVideoError,
    setHasMobileVideoError,
    heroSize,
  } = props;

  const isDesktopViewPort = useMediaQuery(DESKTOP_MEDIA_QUERY);

  //Desktop video should render on both desktop and mobile if only dektop video is enabled.
  //If mobile video is also enabled then desktop video should render only on desktop viewports.
  const shouldRenderDesktopVideo =
    videoEnabledDesktop && !hasDesktopVideoError && (isDesktopViewPort || !videoEnabledMobile);

  //Mobile video should render only when mobile video when enabled
  const shouldRenderMobileVideo = videoEnabledMobile && !isDesktopViewPort && !hasMobileVideoError;

  const videoClickTrackVal = `${location || 'Hero Banner'}|Hero Banner Video Play/Pause Button|internal`;

  const videoOptions = {
    autoplay: enableAutoPlay,
    muted: enableMute || enableAutoPlay,
    aspectRatio: '16:9',
    loop: enableVideoLoop,
    src: playerDomain,
  };

  return (
    <StyledHeroBannerVideo className={clsx('hb__video-component', `mobile-ratio-${aspectRatioMobileVideo}`)}>
      {shouldRenderDesktopVideo && (
        <div className={clsx('hb__video', 'hb-video-desktop', `hero-${heroSize}`)}>
          <Video
            id={`hb-desktop-${videoIdDesktop}`}
            playerId={playerId || 'default'}
            accountId={videoaccountId as string}
            videoId={videoIdDesktop as string}
            poster={fileReferenceVideoCoverImage}
            clickTrack={clickTrack}
            custom_click_track_value={videoClickTrackVal}
            options={{ ...videoOptions }}
            onError={/* istanbul ignore next */ () => setHasDesktopVideoError(true)}
          />
        </div>
      )}
      {shouldRenderMobileVideo && (
        <div className={clsx('hb__video', 'hb-video-mobile', `hero-${heroSize}`)}>
          <Video
            playerId={playerId || 'default'}
            id={`hb-mobile-${videoIdMobile}`}
            accountId={videoaccountId as string}
            videoId={videoIdMobile as string}
            poster={fileReferenceVideoCoverImage}
            clickTrack={clickTrack}
            custom_click_track_value={videoClickTrackVal}
            options={{
              ...videoOptions,
              autoplay: false, // Autoplay for Mobile not passed in existing Hero Banner AEM
            }}
            onError={/* istanbul ignore next */ () => setHasMobileVideoError(true)}
          />
        </div>
      )}
    </StyledHeroBannerVideo>
  );
};

export default HeroBannerVideo;
